import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ideasAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Plus, ThumbsUp, Lightbulb, Loader2, CheckCircle, Clock,
  Rocket, XCircle, Search, TrendingUp, MessageSquare
} from 'lucide-react';
import { format } from 'date-fns';
import { formatDate } from '@/lib/dateUtils';
import Footer from '@/components/Footer';

const STATUS_INFO = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: Lightbulb },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  planned: { label: 'Planned', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-700', icon: Rocket },
  shipped: { label: 'Shipped', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  not_planned: { label: 'Not Planned', color: 'bg-slate-100 text-slate-700', icon: XCircle },
};

const IdeasPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('votes');
  const [showNewIdea, setShowNewIdea] = useState(false);
  const [newIdea, setNewIdea] = useState({ title: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [votingId, setVotingId] = useState(null);

  useEffect(() => {
    loadIdeas();
  }, [filter, sort]);

  const loadIdeas = async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (filter !== 'all') params.status = filter;
      
      const response = await ideasAPI.list(params);
      let loadedIdeas = response.data.ideas;
      
      // Enhancement 1: Pin user's own ideas to the top
      if (user?.id) {
        const userIdeas = loadedIdeas.filter(idea => idea.user_id === user.id);
        const otherIdeas = loadedIdeas.filter(idea => idea.user_id !== user.id);
        // Mark user's ideas as "mine" for visual distinction
        userIdeas.forEach(idea => idea.is_mine = true);
        loadedIdeas = [...userIdeas, ...otherIdeas];
      }
      
      setIdeas(loadedIdeas);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIdea = async () => {
    if (!newIdea.title || !newIdea.description) return;
    setSubmitting(true);
    try {
      await ideasAPI.create(newIdea);
      setShowNewIdea(false);
      setNewIdea({ title: '', description: '' });
      loadIdeas();
    } catch (error) {
      console.error('Failed to submit idea:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (ideaId) => {
    setVotingId(ideaId);
    try {
      const response = await ideasAPI.vote(ideaId);
      setIdeas(ideas.map(idea => 
        idea.id === ideaId 
          ? { ...idea, votes: response.data.votes, user_voted: response.data.user_voted }
          : idea
      ));
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setVotingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold">Feature Ideas</h1>
              <p className="text-sm text-slate-500">Help shape the future of ProjectStatusReport</p>
            </div>
          </div>
          <Button onClick={() => setShowNewIdea(true)} className="gap-2" data-testid="submit-idea-btn">
            <Plus className="h-4 w-4" />
            Submit Idea
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl" data-testid="ideas-page">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-500">Filter:</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ideas</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="not_planned">Not Planned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-500">Sort:</Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="votes">Most Votes</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-slate-500 ml-auto">{total} ideas</span>
        </div>

        {/* Ideas List */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : ideas.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Lightbulb className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No ideas yet</h3>
              <p className="text-slate-500 mb-4">Be the first to submit a feature idea!</p>
              <Button onClick={() => setShowNewIdea(true)}>Submit Idea</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {ideas.map(idea => {
              const statusInfo = STATUS_INFO[idea.status] || STATUS_INFO.new;
              const StatusIcon = statusInfo.icon;
              
              return (
                <Card key={idea.id} className={`hover:shadow-md transition-shadow ${idea.is_mine ? 'ring-2 ring-blue-200 bg-blue-50/30' : ''}`} data-testid={`idea-card-${idea.id}`}>
                  <CardContent className="p-5">
                    <div className="flex gap-4">
                      {/* Vote Button */}
                      <div className="flex flex-col items-center">
                        <Button
                          variant={idea.user_voted ? "default" : "outline"}
                          size="sm"
                          className={`flex flex-col h-auto py-2 px-3 ${idea.user_voted ? 'bg-blue-600' : ''}`}
                          onClick={() => handleVote(idea.id)}
                          disabled={votingId === idea.id}
                          data-testid={`vote-btn-${idea.id}`}
                        >
                          {votingId === idea.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <ThumbsUp className={`h-4 w-4 ${idea.user_voted ? 'text-white' : ''}`} />
                              <span className={`text-lg font-bold ${idea.user_voted ? 'text-white' : ''}`}>
                                {idea.votes}
                              </span>
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{idea.title}</h3>
                            {idea.is_mine && (
                              <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                                Your Idea
                              </Badge>
                            )}
                          </div>
                          <Badge className={`${statusInfo.color} border-0 flex items-center gap-1`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusInfo.label}
                          </Badge>
                        </div>
                        <p className="text-slate-600 mt-2">{idea.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                          <span>by {idea.user_name}</span>
                          <span>•</span>
                          <span>{formatDate(idea.created_at)}</span>
                        </div>
                        {idea.admin_response && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                              <MessageSquare className="h-4 w-4" />
                              Official Response
                            </p>
                            <p className="text-sm text-blue-700 mt-1">{idea.admin_response}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* New Idea Dialog */}
      <Dialog open={showNewIdea} onOpenChange={setShowNewIdea}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-yellow-500" />
              Submit a Feature Idea
            </DialogTitle>
            <DialogDescription>
              Share your ideas to help improve ProjectStatusReport. Other users can vote on ideas they want to see!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="idea-title">Title *</Label>
              <Input
                id="idea-title"
                value={newIdea.title}
                onChange={(e) => setNewIdea({ ...newIdea, title: e.target.value })}
                placeholder="A short, descriptive title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="idea-description">Description *</Label>
              <Textarea
                id="idea-description"
                value={newIdea.description}
                onChange={(e) => setNewIdea({ ...newIdea, description: e.target.value })}
                placeholder="Describe your idea in detail. What problem does it solve? How would it work?"
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewIdea(false)}>Cancel</Button>
            <Button onClick={handleSubmitIdea} disabled={submitting || !newIdea.title || !newIdea.description}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Idea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default IdeasPage;
